import React, { useState } from 'react';
import { ArrowLeft, Eye, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { config } from '../../../../../common/hooks/use-settings-api.hook';
import { buildVisualizerUrl, PREVIEW_LINK_ATTRIBUTION } from '../../../../../common/utils/visualizer-link';

interface StatusBarProps {
  title: string;
  hasChanges: boolean;
  isValid: boolean;
  /** Human-readable validation error messages (shown in tooltip on invalid buttons). */
  validationErrors?: string[];
  isSubmitting: boolean;
  /** Which action triggered the in-flight submission, so only its button spins. */
  submittingAction?: 'save' | 'publish' | null;
  onSave: () => void;
  onDiscard: () => void;
  productSku?: string;
  /** True when the product is a draft (created by an integration sync). */
  isDraft?: boolean;
  /** Called when the vendor clicks "Publish draft". Enabled only when the form is valid. */
  onPublishDraft?: () => void;
}

/**
 * Wraps a submit button in the validation tooltip — but only when there is
 * something to say; an always-mounted Tooltip would pop an empty card. The
 * <span> is deliberate: a disabled button swallows pointer events, so the
 * trigger has to sit outside it. Module-level so remounting the bar's buttons
 * on every keystroke isn't a side effect of rendering.
 */
const ValidationTooltip: React.FC<{
  show: boolean;
  header: string;
  errors: string[];
  children: React.ReactNode;
}> = ({ show, header, errors, children }) => {
  if (!show) return <span>{children}</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>{children}</span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs">
        <p className="mb-1 font-medium">{header}</p>
        <ul className="m-0 ps-4 text-[11px] leading-relaxed">
          {errors.slice(0, 6).map((msg, i) => (
            <li key={i}>{msg}</li>
          ))}
          {errors.length > 6 && <li>+{errors.length - 6} more…</li>}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * Slim status bar with Save/Discard buttons
 * Fixed at top of the page, shows unsaved changes indicator
 */
export const StatusBar: React.FC<StatusBarProps> = ({
  title,
  hasChanges,
  isValid,
  validationErrors = [],
  isSubmitting,
  submittingAction = null,
  onSave,
  onDiscard,
  productSku,
  isDraft,
  onPublishDraft,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // Per-button spinner flags — when a submission is in flight we only spin
  // the button the vendor actually clicked, not both. If submittingAction
  // wasn't set (legacy callers), fall back to spinning Save so we never
  // show a fully-idle state during submission.
  const isSavingNow = isSubmitting && (submittingAction === 'save' || submittingAction === null);
  const isPublishingNow = isSubmitting && submittingAction === 'publish';

  // Dedup + cap so the tooltip doesn't explode when every variant emits the
  // same error. Users only need to see the distinct issues, not every copy.
  const uniqueErrors = Array.from(new Set(validationErrors));
  const showErrorTooltip = !isValid && uniqueErrors.length > 0;

  const handleBack = () => {
    if (hasChanges) {
      setShowUnsavedDialog(true);
    } else {
      navigate('/products/catalog');
    }
  };

  const handleConfirmLeave = () => {
    setShowUnsavedDialog(false);
    navigate('/products/catalog');
  };

  const handleCancelLeave = () => {
    setShowUnsavedDialog(false);
  };

  return (
    <div className="sticky top-3 z-40 mb-6 flex items-center justify-between border bg-background/85 px-6 py-3 backdrop-blur-md">
      {/* Left side: Back button + Title */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={handleBack}
          aria-label={t('PAGES.NEW_PRODUCT.V3.BACK_TO_PRODUCTS') as string}
        >
          <ArrowLeft />
        </Button>

        <div>
          {/* Context label — swaps between "Products" and unsaved indicator */}
          {hasChanges ? (
            <div className="mb-1.5 flex items-center gap-1.5">
              <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-warning" />
              <span className="text-xs font-medium leading-none text-warning">
                {t('PAGES.NEW_PRODUCT.V2.UNSAVED_CHANGES')}
              </span>
            </div>
          ) : (
            <p className="mb-1 text-xs font-medium leading-none text-muted-foreground">
              {t('PAGES.NEW_PRODUCT.V3.STATUS_BAR.PRODUCTS')}
            </p>
          )}

          <p className="font-heading text-sm font-medium leading-snug tracking-tight text-foreground">
            {title || t('PAGES.NEW_PRODUCT.V3.STATUS_BAR.NEW_PRODUCT')}
          </p>
        </div>
      </div>

      {/* Right side: Action buttons */}
      <div className="flex items-center gap-2">
        {/* Draft products aren't visible in the visualizer yet, so hide the
            preview button entirely — clicking it would just show a blank
            result and confuse the vendor. */}
        {productSku && config?.alias && !isDraft && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                className="h-9"
                onClick={() => {
                  const url = buildVisualizerUrl({ alias: config.alias, sku: productSku, attribution: PREVIEW_LINK_ATTRIBUTION });
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
              >
                <Eye data-icon="inline-start" />
                {t('PAGES.MY_PRODUCTS.MENU.PREVIEW')}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t('PAGES.MY_PRODUCTS.MENU.PREVIEW')}</TooltipContent>
          </Tooltip>
        )}
        {hasChanges && (
          <Button variant="ghost" className="h-9 text-muted-foreground" onClick={onDiscard} disabled={isSubmitting}>
            {t('PAGES.NEW_PRODUCT.V2.DISCARD')}
          </Button>
        )}

        <ValidationTooltip
          show={showErrorTooltip}
          header={t('PAGES.NEW_PRODUCT.V3.VALIDATION_ERRORS_HEADER', { count: uniqueErrors.length })}
          errors={uniqueErrors}
        >
          <Button
            variant="outline"
            className={cn('h-9', !isValid && hasChanges && 'border-warning/60')}
            onClick={onSave}
            disabled={!hasChanges || isSubmitting}
          >
            {isSavingNow && <Loader2 data-icon="inline-start" className="animate-spin" />}
            {isSavingNow ? t('PAGES.NEW_PRODUCT.V2.SAVING') : t('COMMON.SAVE')}
            {!isValid && hasChanges && uniqueErrors.length > 0 && (
              <span className="ms-1 bg-warning px-1 text-[10px] leading-[14px] font-medium text-white">
                {uniqueErrors.length}
              </span>
            )}
          </Button>
        </ValidationTooltip>

        {/* Publish draft — only rendered for draft products (typically
            created by an integration sync). Flips isDraft off and makes
            the product visible. Enabled only when the form is valid. */}
        {isDraft && onPublishDraft && (
          <ValidationTooltip
          show={showErrorTooltip}
          header={t('PAGES.NEW_PRODUCT.V3.VALIDATION_ERRORS_HEADER', { count: uniqueErrors.length })}
          errors={uniqueErrors}
        >
            <Button
              className="h-9 bg-success text-white hover:bg-success/90"
              onClick={onPublishDraft}
              disabled={isSubmitting}
            >
              {isPublishingNow && <Loader2 data-icon="inline-start" className="animate-spin" />}
              {t('PAGES.NEW_PRODUCT.V3.PUBLISH_DRAFT', 'Publish draft')}
              {!isValid && uniqueErrors.length > 0 && (
                <span className="ms-1 bg-white/25 px-1 text-[10px] leading-[14px] font-medium text-white">
                  {uniqueErrors.length}
                </span>
              )}
            </Button>
          </ValidationTooltip>
        )}
      </div>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog
        open={showUnsavedDialog}
        onOpenChange={(next) => {
          if (!next) handleCancelLeave();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('PAGES.NEW_PRODUCT.V2.UNSAVED_CHANGES_TITLE')}</DialogTitle>
            <DialogDescription>
              {t('PAGES.NEW_PRODUCT.V2.UNSAVED_CHANGES_MESSAGE')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelLeave}>
              {t('COMMON.CANCEL')}
            </Button>
            <Button
              variant="outline"
              onClick={handleConfirmLeave}
              className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
            >
              {t('PAGES.NEW_PRODUCT.V2.LEAVE_WITHOUT_SAVING')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
